import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import {
  DatePicker,
  FieldWrapperVertical,
  SnackbarApiErrorMessage,
  TimePicker,
  useDataProvider,
  useDialog,
  useLocalization,
  useMasterData,
  useSnackbar,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { nanoid } from 'nanoid';
import DialogCloseButton from './DialogCloseButton';
import CustomerServiceRequests from '../FormFields/CustomerServiceRequests';
import IconLibrary from '../IconLibrary';

function RescheduleJobDialog({
  open,
  onClose,
  jobId,
  providerId,
  recordVersion,
  initialData = {},
  ...props
}) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, post, put, get } = useDataProvider(provider);
  const { confirmationRef } = useDialog();
  const {
    cancellableJobStatuses: validCancelStatuses,
    reschedulableJobStatuses: validRescheduleStatuses,
  } = useMasterData();

  const [view, setView] = useState('');
  const [serviceJob, setServiceJob] = useState(null);
  const [loading, setLoading] = useState(false);

  function closeDialog() {
    onClose(null);
  }

  const buttons = [
    {
      label: formatMessage({ id: 'YES', defaultMessage: 'Yes' }),
      onClick: () => setView('RESCHEDULE'),
      variant: 'contained',
      color: 'primary',
      show: ['MAIN'],
      startIcon: <IconLibrary.Yes />,
    },
    {
      label: formatMessage({ id: 'NO', defaultMessage: 'No' }),
      onClick: () => setView('REDISPATCH'),
      variant: 'contained',
      disabled: !validCancelStatuses.includes(serviceJob?.serviceJobStatus),
      color: 'primary',
      show: ['MAIN'],
      startIcon: <IconLibrary.No />,
    },
    {
      label: formatMessage({
        id: 'RESCHEDULE',
        defaultMessage: 'Reschedule',
      }),
      variant: 'contained',
      color: 'primary',
      type: 'submit',
      name: 'RESCHEDULE',
      show: ['RESCHEDULE'],
      disabled: !validRescheduleStatuses.includes(serviceJob?.serviceJobStatus),
      tooltip: 'Cannot reschedule job in this status',
      startIcon: <IconLibrary.Reschedule />,
    },
    {
      label: formatMessage({
        id: 'RESCHEDULE',
        defaultMessage: 'Reschedule',
      }),
      variant: 'contained',
      color: 'primary',
      type: 'submit',
      name: 'REDISPATCH',
      show: ['REDISPATCH'],
      disabled: !validRescheduleStatuses.includes(serviceJob?.serviceJobStatus),
      tooltip: 'Cannot reschedule job in this status',
      startIcon: <IconLibrary.Reschedule />,
    },
    {
      label: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: () => {
        closeDialog();
      },
      variant: 'contained',
      color: 'secondary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  const validationSchema = yup.object().shape({
    serviceDate: yup.string().when('view', {
      is: () => view === 'RESCHEDULE',
      then: schema =>
        schema
          .required(
            formatMessage({
              id: 'SERVICE_DATE_REQUIRED',
              defaultMessage: 'Date is required',
            }),
          )
          .test(
            'is-date-in-past',
            formatMessage({
              id: 'APPOINTMENT_DATE_PAST',
              defaultMessage: 'Date cannot be in the past',
            }),
            value => {
              return !dayjs(value).isBefore(dayjs(), 'day');
            },
          ),
      otherwise: schema => schema.notRequired(),
    }),
    customerServiceRequests: yup.array().of(
      yup.object().shape({
        requestDate: yup.string().when('view', {
          is: () => view === 'REDISPATCH',
          then: schema =>
            schema
              .required(
                formatMessage({
                  id: 'REQUEST_DATE_REQUIRED',
                  defaultMessage: 'Date is required',
                }),
              )
              .test(
                'is-date-in-past',
                formatMessage({
                  id: 'APPOINTMENT_DATE_PAST',
                  defaultMessage: 'Date cannot be in the past',
                }),
                value => {
                  return !dayjs(value).isBefore(dayjs(), 'day');
                },
              ),
          otherwise: schema => schema.notRequired(),
        }),
      }),
    ),
  });

  const fetchServiceJob = async () => {
    try {
      const url = `/sxcl/v1/service-jobs/${jobId}`;
      const response = await get(url);
      if (response?.ok) {
        return response.data;
      }
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    } catch (err) {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={[{ message: err.message }]} />,
      });
    }
    return null;
  };

  async function rescheduleJob(data) {
    setLoading(true);
    const body = {
      serviceDate: data.serviceDate,
      serviceDateAfterHour: data.serviceDateAfterHour,
      serviceDateBeforeHour: data.serviceDateBeforeHour,
      jobRecordVersion: recordVersion,
    };
    const response = await post(
      `${getApiUrl()}/sxcl/v1/service-providers/${providerId}/pending-acceptance/${jobId}/assign`,
      body,
    );
    if (response.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'RESCHEDULE_JOB_SUCCESS',
          defaultMessage: 'Job was rescheduled successfully',
        }),
      );
      onClose(nanoid());
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
    setLoading(false);
  }

  const submitRedispatch = async data => {
    setLoading(true);
    const body = {
      recordVersion: serviceJob?.recordVersion,
      customerServiceRequests: data?.customerServiceRequests,
    };
    const response = await put(
      `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/reschedule`,
      body,
    );
    if (response.ok) {
      confirmationRef.current.init({
        message: 'Job was rescheduled successfully',
        positiveButtonText: 'Close',
        width: 360,
        title: 'Job Reschedule',
        onChange: () => {
          confirmationRef.current.setOpen(false);
          onClose(nanoid());
        },
      });
      confirmationRef.current.setOpen(true);
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
    setLoading(false);
  };

  function onFormSubmit(data, button) {
    const buttonName = button?.nativeEvent?.submitter?.name;
    switch (buttonName) {
      case 'RESCHEDULE': {
        rescheduleJob(data);
        break;
      }
      case 'REDISPATCH': {
        submitRedispatch(data);
        break;
      }
      default:
    }
  }

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initialData,
  });

  const { control, handleSubmit } = methods;

  const initialize = async () => {
    setLoading(true);
    if (open) {
      methods.reset();
    }
    if (providerId) {
      setView('MAIN');
    } else {
      setView('REDISPATCH');
    }
    if (open) {
      const sj = await fetchServiceJob();
      setServiceJob(sj);
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, [open]);

  return (
    <Dialog open={open} onClose={() => closeDialog()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.RescheduleServiceJob />
        {formatMessage({
          id: 'RESCHEDULE_JOB',
          defaultMessage: 'Reschedule Job',
        })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {serviceJob?.serviceJobNumber}
        </Typography>
        <DialogCloseButton disabled={loading} onClose={() => closeDialog()} />
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogContent>
            {loading ? (
              <Box textAlign="center">
                <CircularProgress size="30px" />
                <Typography>Processing</Typography>
              </Box>
            ) : (
              <>
                {view === 'MAIN' && (
                  <>
                    Do you wish to reschedule Job {serviceJob?.serviceJobNumber}{' '}
                    with the current Service Provider?
                  </>
                )}
                {view === 'RESCHEDULE' && (
                  <Grid2 container spacing={1}>
                    <Grid2 xs={5}>
                      <FieldWrapperVertical
                        field={{
                          name: 'serviceDate',
                          label: {
                            localeId: 'SERVICE_DATE_LABEL',
                            defaultMessage: 'Service Date',
                          },
                        }}
                        errors={methods.formState.errors}>
                        <DatePicker disablePast name="serviceDate" />
                      </FieldWrapperVertical>
                    </Grid2>
                    <Grid2 xs={3}>
                      <Controller
                        name="serviceDateAfterHour"
                        control={control}
                        render={({ field }) => (
                          <FieldWrapperVertical
                            field={{
                              name: 'serviceDateAfterHour',
                              label: {
                                localeId: 'SERVICE_DATE_AFTER_HOUR_LABEL',
                                defaultMessage: 'After',
                              },
                            }}>
                            <TimePicker {...field} />
                          </FieldWrapperVertical>
                        )}
                      />
                    </Grid2>
                    <Grid2 xs={3}>
                      <Controller
                        name="serviceDateBeforeHour"
                        control={control}
                        render={({ field }) => (
                          <FieldWrapperVertical
                            field={{
                              name: 'serviceDateBeforeHour',
                              label: {
                                localeId: 'SERVICE_DATE_BEFORE_HOUR_LABEL',
                                defaultMessage: 'Before',
                              },
                            }}>
                            <TimePicker {...field} />
                          </FieldWrapperVertical>
                        )}
                      />
                    </Grid2>
                  </Grid2>
                )}
                {view === 'REDISPATCH' && (
                  <Box>
                    <CustomerServiceRequests
                      field={{
                        name: 'customerServiceRequests',
                      }}
                      permission={{
                        ...props?.permissions?.customerServiceRequests,
                      }}
                      minWidth="auto"
                    />
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions
            display="flex"
            sx={{ justifyContent: 'center', my: 2 }}>
            {buttons
              .filter(btn => !btn.show || btn.show.includes(view))
              .map(btn => {
                const { label, tooltip, ...rest } = btn;
                return (
                  <Tooltip
                    key={label}
                    title={
                      validRescheduleStatuses.includes(
                        serviceJob?.serviceJobStatus,
                      )
                        ? ''
                        : tooltip
                    }>
                    <span>
                      <Button key={label} disabled={loading} {...rest}>
                        {label}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })}
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default RescheduleJobDialog;
